import { FC, useEffect, useState } from "react";
import AppLayout from "../../components/layout/app-layout/app-layout";
import Tabs from "../../components/shared/Tabs";
import moment from "moment";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import Card from "../../components/shared/Card/card";
import styles from "./page-marketing.module.scss";
import ReceivedBidRequestStats from "../page-bid-request-list/ReceivedRequestsStats";
import { ServiceQuotation } from "../../services/service-quotation";
import { Loading } from "../../components/shared";
import { InvoiceInterval, Request } from "../page-bid-request-list/types";
import { ServiceSupplier } from "../../services/service-supplier";
import TitleWrapper from "../../components/shared/TitleWrapper/TitleWrapper";
import BtButton from "../bt-button/bt-button";
import { serviceAreas } from "../../services/service-areas";
import { serviceAdApplication } from "../../services/service-ad-application";
import _ from "lodash";
import { SelectorType } from "../../components/shared/Selector/Selector";
import { NotificationManager } from "react-notifications";
import * as Sentry from "@sentry/react";
import { AdModal } from "./ad-modal";
import { AdApplicationStatus, AdApplicationType } from "../../util/appEnum";
import Badge from "../../components/shared/Badge/Badge";
import NewsCard from "../page-dashboard-V2/NewsCard/NewsCard";
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import appState from "../../state/AppStateContainer";
import { useTranslation, Trans } from "react-i18next";
import OpportunityCard from "../../components/shared/OpportunityCard/OpportunityCard";
import { ReactComponent as LightningIcon } from "../../assets/svg/thunderIcon.svg";
import { ReactComponent as structureTreeIcon } from "../../assets/svg/structureTree.svg";
import { ReactComponent as paperListIcon } from "../../assets/svg/paperList.svg";
import { ReactComponent as dashboardIcon } from "../../assets/svg/dashboard.svg";
import { ReactComponent as securityIcon } from "../../assets/svg/security.svg";
import { ReactComponent as SuccessSignUpIcon } from '../../assets/svg/successSignUpIcon.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/InfoIconLight.svg';
import { ServiceWebflow } from "../../services/service-webflow";
import Marquee from "react-fast-marquee";

type AdInfoType = {
  ads?: {
    data: {
      _id: string,
      startDate?: string,
      endDate?: string,
      regions?: string[],
      areas?: string[],
    }[],
    earliestStartDateOfAnAd?: string,
  },
  isFeatured: boolean,
  featuredInfo?: {
    url: string,
    image: string,
    title: string,
    description: string,
    startDate: Date,
    endDate: Date,
  },
  addedSuppliers?: {
    name: string,
    createdAt: string,
  }[],
  addedFeaturedSuppliers?: {
    name: string,
    createdAt: string,
  }[],
  supplierId: string,
}

export type AdApplication = {
  organizationNumber?: string,
  errors?: {[x: string]: string},
  startDate?: Date,
  areas?: {id: string, label: string, value: string}[],
  regions?: SelectorType[],
  title?: string,
  url?: string,
  image?: {
    file: string,
  } | File,
  description?: string,
  applicationType?: AdApplicationType,
  invoiceInterval?: InvoiceInterval,
  status?: AdApplicationStatus,
  rejectionReason?: string,
  invoiceEmail?: string,
  invoiceRef?: string,
}

type Params = {};
type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  history: RouteTypes['history'];
}

const formatDate = (date: string | Date | undefined) => {
  return date && moment(date).locale('sv').format('D MMMM YYYY');
};

const PageMarketing: FC<Props> = ({ history }) => {
  history;
  const [selectedTab, setSelectedTab] = useState(0);
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [adInfo, setAdInfo] = useState<AdInfoType>();
  const [adApplication, setAdApplication] = useState<AdApplication>();
  const [areas, setAreas] = useState<{id: string, label: string, value: string}[]>([]);
  const [isApplicationModalVisible, setIsApplicationModalVisible] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [adApplications, setAdApplications] = useState<AdApplication[]>([]);
  const [webflowLogos, setWebflowLogos] = useState<any[]>([]);
  const [webflowLoading, setWebflowLoading] = useState(true);
  const isProfileComplete = appState.hasSupplierProfileBeenUpdated();
  const [t] = useTranslation('marketing');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setWebflowLoading(true);
      const response = await ServiceQuotation.fetchAllReceived();
      setRequests(response.data);

      const adInfo = await ServiceSupplier.fetchRecommendedSupplierAdInfo();
      setAdInfo(adInfo);

      const adApplications = await serviceAdApplication.fetchLocalAdApplications();
      setAdApplications(adApplications);

      const areasData: {_id: string, name: string}[] = await serviceAreas.fetchSuperAdminAreas();
      const areas: {id: string, label: string, value: string}[] = areasData.map(item => ({ id: item._id, label: item.name, value: item.name }));

      setAreas(areas);

      const webflowLogosData = await ServiceWebflow.getWebflowLogos();
      const webflowLogos = webflowLogosData.data.filter((logo: any) => !logo.isArchive && !logo.isDraft && logo.fieldData?.logo?.url).map((logo: any) => ({
        url: logo.fieldData?.logo?.url,
        name: logo.fieldData?.name,
      }));
      setWebflowLogos(webflowLogos);
      setWebflowLoading(false);
      setLoading(false);
    })();
  }, []);

  const submitRecommendedSupplierAd = async () => {
    setSubmitting(true);
    try {
      await serviceAdApplication.createRecommendedAdApplication({
        ad: {
          startDate: adApplication?.startDate!,
          regions: (adApplication?.regions ?? []).map(x => x?.value ?? '').filter(x => x),
          areas: (adApplication?.areas ?? []).map(x => x?.id ?? '').filter(x => x),
        },
        invoiceInterval: adApplication?.invoiceInterval!,
        invoiceEmail: adApplication?.invoiceEmail!,
        invoiceRef: adApplication?.invoiceRef!,
      });
      setAdApplication(undefined);
      setIsApplicationModalVisible(false);
      const adApplications = await serviceAdApplication.fetchLocalAdApplications();
      setAdApplications(adApplications);
      await appState.fetchOrganizations();

      NotificationManager.success(
        t('notification.success.description', 'Din ansökan har lämnats'),
        t('notification.success.title', 'Ansökt')
      );
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', t('notification.error.description', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.title', 'Misslyckat'));
      Sentry.captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitFeaturedSupplierAd = async () => {
    setSubmitting(true);
    try {
      await serviceAdApplication.createFeaturedAdApplication({
        featuredInfo: {
          startDate: adApplication?.startDate!,
          url: adApplication?.url!,
          title: adApplication?.title!,
          image: (adApplication?.image! as File),
          description: adApplication?.description!,
        },
        invoiceInterval: adApplication?.invoiceInterval!,
        invoiceEmail: adApplication?.invoiceEmail!,
        invoiceRef: adApplication?.invoiceRef!,
      });
      setAdApplication(undefined);
      setIsApplicationModalVisible(false);
      const adApplications = await serviceAdApplication.fetchLocalAdApplications();
      setAdApplications(adApplications);
      await appState.fetchOrganizations();
      NotificationManager.success(
        t('notification.success.description', 'Din ansökan har lämnats'),
        t('notification.success.title', 'Ansökt')
      );
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', t('notification.error.description', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.title', 'Misslyckat'));
      Sentry.captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const lastRecommendedApplication = adApplications?.find(x => x?.applicationType === AdApplicationType.RECOMMENDED);
  const isRecommendedApplicationPending = lastRecommendedApplication?.status === AdApplicationStatus.PENDING;
  const lastFeaturedApplication = adApplications?.find(x => x?.applicationType === AdApplicationType.FEATURED);
  const isFeaturedApplicationPending = lastFeaturedApplication?.status === AdApplicationStatus.PENDING;

  type AdApplicationStatusCardProps = {
    isPending: boolean,
    applicationType: AdApplicationType,
    rejectionReason: string,
  };

  const ApplicationStatusCard: FC<AdApplicationStatusCardProps> = ({ isPending, rejectionReason }) => {
    return (
      <Card title={
        <span style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--gray-700)' }}>
          <span>{t('applicationStatus.heading.label', 'Ansökningsstatus:')} {isPending ? t('applicationStatus.heading.pending', 'pågående') : t('applicationStatus.heading.rejected', 'nekad')}</span>
        </span>
      }>
        {isPending ? (
          <>
            <div style={{ display: 'grid', marginTop: '8px', fontSize: '14px', color: 'var(--gray-700)' }}>
              <Trans t={t} i18nKey="applicationStatus.pending.intro" components={{span: <span />}} />
            </div>

            <p style={{ fontSize: '14px', color: 'var(--gray-700)' }}>{t('applicationStatus.pending.outro', 'Om du vill avbryta eller ändra din ansökan, kontakta support@accurator.se.')}</p>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', marginTop: '8px', fontSize: '14px', color: 'var(--gray-700)' }}>
              <span>{t('applicationStatus.rejected.intro', 'Din ansökan har nekats.')}</span>
              {rejectionReason && <span>{t('applicationStatus.rejected.label.reason', 'Anledning')}: {rejectionReason}</span>}
            </div>

            <p style={{ fontSize: '14px', color: 'var(--gray-700)' }}>{t('applicationStatus.rejected.outro', 'Kontakta oss om du vill ansöka igen.')}</p>
          </>
        )}
      </Card>
    )
  }

  type SubscriptionStatusCardProps = {
    hasPendingApplication: boolean,
    hasRejectedApplication: boolean,
  };

  const SubscriptionStatusCard: FC<SubscriptionStatusCardProps> = ({ hasPendingApplication, hasRejectedApplication }) => {
    if (hasPendingApplication) {
      return (
        <Card title={
          <span style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--gray-700)' }}>
            <span>{t('applicationStatus.heading.label', 'Ansökningsstatus:')} {t('applicationStatus.heading.pending', 'pågående')}</span>
          </span>
        }>
          <div style={{ display: 'grid', marginTop: '8px', fontSize: '14px', color: 'var(--gray-700)' }}>
            <Trans t={t} i18nKey="applicationStatus.pending.intro" components={{span: <span />}} />
          </div>
          <p style={{ fontSize: '14px', color: 'var(--gray-700)' }}>
            {t('applicationStatus.pending.outro', 'Om du vill avbryta eller ändra din ansökan, kontakta support@accurator.se.')}
          </p>
        </Card>
      )
    }

    if (hasRejectedApplication) {
      return (
        <Card title={
          <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span>{t('applicationStatus.heading.label', 'Ansökningsstatus:')} {t('applicationStatus.heading.rejected', 'nekad')}</span>
          </span>
        }>
          <div style={{ display: 'grid', marginTop: '8px', fontSize: '14px', color: 'var(--gray-700)' }}>
            <span>{t('applicationStatus.rejected.intro', 'Din ansökan har nekats.')}</span>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--gray-700)' }}>
            {t('applicationStatus.rejected.outro', 'Kontakta oss om du vill ansöka igen.')}
          </p>
        </Card>
      )
    }

    return null;
  }

  const hasActiveRecommendedAd = !!adInfo?.ads?.data?.length;
  const hasActiveFeaturedAd = adInfo?.isFeatured;

  const featureCards = [
    {
      title: t('features.recommendedSupplier.title', 'Rekommenderad leverantör'),
      description: '',
      icon: structureTreeIcon,
      features: [
        { title: t('features.recommendedSupplier.features.visibility.title', 'Synlighet'), description: t('features.recommendedSupplier.features.visibility.description', 'Ni markeras som Rekommenderad leverantör i inköparnas register för era kategorier och län.'), icon: SuccessSignUpIcon, isInfo: false },
        { title: t('features.recommendedSupplier.features.leadGeneration.title', 'Rekommenderad'), description: t('features.recommendedSupplier.features.leadGeneration.description', 'När en inköpare skapar en förfrågan inom era kategorier föreslås ni direkt i valet av leverantörer som ska bjudas in.'), icon: SuccessSignUpIcon, isInfo: false }      ]
    },
    {
      title: t('features.centralizedInquiries.title', 'Samla alla förfrågningar'),
      description: '',
      icon: paperListIcon,
      features: [
        { title: t('features.centralizedInquiries.features.platform.title', 'Kontroll'), description: t('features.centralizedInquiries.features.platform.description', 'Visar samtliga förfrågningar som skickats till ert företag. Behöriga användare ser dem oavsett vilken kollega som mottog förfrågan eller är inloggad'), icon: SuccessSignUpIcon, isInfo: false },
        { title: t('features.centralizedInquiries.features.efficiency.title', 'Missa inga affärsmöjligheter'), description: t('features.centralizedInquiries.features.efficiency.description', 'Alla förfrågningar som skickas till ert organisationsnummer samlas i en vy. Inget faller mellan stolarna.'), icon: SuccessSignUpIcon, isInfo: false },
      ]
    },
    {
      title: t('features.resultsDashboard.title', 'Resultatdashboard'),
      description: '',
      icon: dashboardIcon,
      features: [
        { title: t('features.resultsDashboard.features.measurement.title', 'Mät effekten i realtid'), description: t('features.resultsDashboard.features.measurement.description', 'Mät utvecklingen före/efter aktivering: förfrågningar, lämnade anbud och vunna jobb.'), icon: SuccessSignUpIcon, isInfo: false },
        { title: t('features.resultsDashboard.features.conversion.title', 'Nya inköpskunder'), description: t('features.resultsDashboard.features.conversion.description', 'Se vilka inköpare som lagt till er i sitt huvudregister och hur kundbasen växer över tid.'), icon: SuccessSignUpIcon, isInfo: false },
      ]
    },
    {
      title: t('features.publishedRequests.title', 'Publicerade förfrågningar'),
      description: '',
      icon: securityIcon,
      features: [
        { title: t('features.publishedRequests.features.findJobs.title', 'Hitta nya jobb'), description: t('features.publishedRequests.features.findJobs.description', 'Sök och filtrera publicerade förfrågningar som matchar er profil.'), icon: SuccessSignUpIcon, isInfo: false },
        { title: t('features.publishedRequests.features.monitoring.title', 'Bevakning'), description: t('features.publishedRequests.features.monitoring.description', 'Skapa bevakningar och få notiser när nya förfrågningar publiceras i era kategorier och län.'), icon: SuccessSignUpIcon, isInfo: false }
      ]
    }
  ];

  const parsePrice = (price: string) => {
    const slashIndex = price.indexOf('/');
    
    if (slashIndex !== -1) {
      const beforeSlash = price.substring(0, slashIndex).trim();
      const afterSlash = price.substring(slashIndex);
      
      // Split the amount to separate number from currency
      const amountParts = beforeSlash.split(' ');
      if (amountParts.length >= 2) {
        const number = amountParts.slice(0, -1).join(' ');
        const currency = amountParts[amountParts.length - 1]; 
        return {
          amount: number,
          currency: currency,
          unit: afterSlash
        };
      } else {
        return {
          amount: beforeSlash,
          unit: afterSlash
        };
      }
    } else {
      return {
        amount: price,
        unit: null
      };
    }
  };

  const pricingCards = [
    {
      title: t('pricing.oneMonth.title', '1 månad'),
      description: t('pricing.oneMonth.description', 'Tre månaders uppsägningstid'),
      price: '2 999 kr/månad',
      buttonText: t('pricing.oneMonth.buttonText', 'Ansök om att bli Prio-leverantör'),
      secondaryButtonText: t('pricing.oneMonth.secondaryButtonText', 'Boka genomgång')
    },
    {
      title: t('pricing.twelveMonths.title', '12 månader'), 
      description: t('pricing.twelveMonths.description', 'Förnyas automatiskt ett år i taget'),
      price: '2 399 kr/månad',
      buttonText: t('pricing.twelveMonths.buttonText', 'Ansök om att bli Prio-leverantör'),
      secondaryButtonText: t('pricing.twelveMonths.secondaryButtonText', 'Boka genomgång')
    }
  ];

  const faqItems = [
    {
      question: t('faq.differenceFromFree.question', 'Vad är skillnaden mot gratisversionen?'),
      answer: t('faq.differenceFromFree.answer', 'Som Prio-leverantör får ni extra synlighet och visas som rekommenderad när inköpare söker leverantörer. Ni får också tillgång till alla förfrågningar på en plattform och kan mäta era resultat.')
    },
    {
      question: t('faq.quickEffect.question', 'Hur snabbt ser vi effekt?'),
      answer: t('faq.quickEffect.answer', 'Dashboardet mäter direkt från aktivering, så ni kan följa era resultat från dag ett. Effekten på synlighet och antal förfrågningar kommer att vara märkbar inom de första veckorna.')
    },
    {
      question: t('faq.teamAccess.question', 'Behöver alla i teamet Prio?'),
      answer: t('faq.teamAccess.answer', 'Prio gäller för hela företaget, så alla i ert team kan se och hantera förfrågningar. Ni bestämmer själva vem som ska ha tillgång till olika delar av systemet.')
    }
  ];

  const tabs = [t('tabs.prio', 'Prio-leverantör'), t('tabs.adApplication', 'Annonsera på startsidan')];

  return (
    <AppLayout title={
      <div className={styles.pageTitle}>
        <LightningIcon className={styles.pageTitleIcon} />
        <span>{t('pageTitle', 'Bli Accurator Prio-leverantör')}</span>
      </div>
    }>
      <div style={{ margin: "32px" }}>
        <div className="tab-container">
          <Tabs
            tabs={tabs}
            selected={selectedTab}
            onChange={setSelectedTab}
          />
          <div className="tab-actions">
          </div>
        </div>
        {loading && <div style={{ width: '100%', display: 'grid', placeItems: 'center' }}><Loading type='inline' /></div>}
        {selectedTab === 0 && !loading && (
          <>
            {hasActiveRecommendedAd ? (
              <>
                <Card title={t('prio.activeAds.title', 'Aktiva annonser')} className={styles.activeRecommendedAds}>
                  {adInfo.ads?.data.map(ad => {
                    return (
                      <div className={styles.activeRecommendedAdsItem} key={ad._id}>
                        <p>{t('prio.activeAds.label.date', 'Datum')}</p>
                        <span>{formatDate(ad.startDate)} - {formatDate(ad.endDate)}</span>
                        <p>{t('prio.activeAds.label.region', 'Län')}</p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: "wrap" }}>{ad.regions?.map(region => <Badge key={region}>{region}</Badge>)}</div>
                        <p>{t('prio.activeAds.label.area', 'Kategorier')}</p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: "wrap" }}>{ad.areas?.map(area => <Badge key={area}>{area}</Badge>)}</div>
                      </div>
                    )
                  })}
                </Card>

                <TitleWrapper title={t('prio.statistics.title', 'Statistik')}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {adInfo?.ads?.earliestStartDateOfAnAd && (
                      <ReceivedBidRequestStats requests={requests} adStartDate={moment(adInfo?.ads?.earliestStartDateOfAnAd).toDate()} />
                    )}
                  </div>
                </TitleWrapper>

                <Card title={t('prio.activeRecommendedAd.title', 'Resultat')} className={styles.result}>
                  {adInfo?.addedSuppliers?.length ? (<>
                    <p>{t('prio.activeRecommendedAd.intro', 'Du har lagts till i följande leverantörsdatabaser sedan annonsens början:')}</p>
                    <table style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th>{t('prio.activeRecommendedAd.header.supplierName', 'Beställare')}</th>
                          <th>{t('prio.activeRecommendedAd.header.createdAt', 'Datum')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adInfo?.addedSuppliers?.map(supplier => {
                          return (
                            <tr key={supplier.name}>
                              <td>{supplier.name}</td>
                              <td>{formatDate(supplier.createdAt)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    <p>{t('prio.activeRecommendedAd.outro', 'Se även "Mottagna förfrågningar" i menyn till vänster för att se detaljer om förfrågningar och anbud.')}</p>
                  </>) : (
                    <p>{t('prio.activeRecommendedAd.emptyMessage', 'Du har inte lagts till någon leverantörsdatabas sedan annonsens början. Se "Mottagna förfrågningar" i menyn till vänster för att se detaljer om förfrågningar och anbud.')}</p>
                  )}
                </Card>
              </>
            ) : (
              <>
            <div className={styles.section}>
              <div className={styles.sectionContent}>
                <div className={styles.sectionTitle}>{t('header.subtitle', 'Kontroll över alla förfrågningar och extra synlighet mot inköpare')}</div>
                <p className={styles.description}>
                  {t('header.description', 'Prio samlar alla era förfrågningar på ett ställe och lyfter fram er som rekommenderad leverantör när inköpare ska köpa. Ni ser mer, missar mindre och kan mäta resultatet.')}
                </p>
              </div>
            </div>
            
            <SubscriptionStatusCard 
              hasPendingApplication={isRecommendedApplicationPending}
              hasRejectedApplication={!!lastRecommendedApplication && !isRecommendedApplicationPending}
            />
            
            {!isRecommendedApplicationPending && !lastRecommendedApplication && (
              <>
                <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('header.sectionTitle', 'Utöka ditt nätverk och ta emot fler förfrågningar som Prio-leverantör')}</div>
              <div className={styles.featuresGrid}>
                {featureCards.map((card, index) => (
                  <OpportunityCard
                    key={index}
                    title={card.title}
                    description={card.description}
                    icon={card.icon}
                    features={card.features}
                    fieldClassName={styles.marketingCard}
                  />
                ))}
              </div>
            </div>
            <div className={styles.section}>
                        <div className={styles.sectionTitle}>{t('header.logosTitle', 'Här är några bygg- och fastighetsbolag som använder Accurator i sina upphandlingar')}</div>
                        <div className={styles.sectionLogos}>
                          <Marquee
                            pauseOnHover={true}
                            direction="left"
                            speed={85}
                            gradient={true}
                            gradientWidth={75}
                            gradientColor="#f3f4f6"
                            className={styles.sectionLogosMarquee}
                          >
                            {webflowLogos.map((logo, index) => (
                              <div className={styles.sectionLogosItem} key={logo.name + index}>
                                <img
                                  src={logo.url}
                                  alt={logo.name}
                                />
                              </div>
                            ))}

                          </Marquee>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('pricing.title', 'Pris & avtal')}</div>
              <div className={styles.sectionContent}>
                <div className={styles.pricingGrid}>
                  {pricingCards.map((card, index) => (
                    <div key={index} className={styles.pricingCard}>
                      <div className={styles.pricingCardTitle}>
                        {card.title}
                        <TooltipContainer renderReferenceComponent={(className, ref) => <InfoIcon className={className} ref={ref} style={{ marginLeft: "8px", verticalAlign: "bottom" }} />}>
                          {card.description}
                        </TooltipContainer>
                      </div>
                      
                      <div className={styles.pricingCardPrice}>
                        {(() => {
                          const parsedPrice = parsePrice(card.price);
                          return (
                            <>
                              <span className={styles.priceAmount}>{parsedPrice.amount}</span>
                              {parsedPrice.currency && (
                                <span className={styles.priceCurrency}>{parsedPrice.currency}</span>
                              )}
                              {parsedPrice.unit && (
                                <span className={styles.priceUnit}>{parsedPrice.unit}</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.pricingButtons}>
                  <TooltipContainer renderReferenceComponent={(className, ref) => (
                    <div
                      className={className}
                      ref={ref}>
                        <BtButton
                          disabled={!isProfileComplete}
                          onClick={() => {
                            setIsApplicationModalVisible(true);
                            setAdApplication({
                              errors: {},
                              startDate: moment().toDate(),
                              regions: [],
                              areas: [],
                              applicationType: AdApplicationType.RECOMMENDED,
                              invoiceInterval: undefined,
                              invoiceEmail: appState.getInvoiceEmail(),
                              invoiceRef: appState.getInvoiceRef(),
                            })
                          }}
                          color=""
                          size="base"
                        >
                          {t('pricing.oneMonth.buttonText', 'Ansök om att bli Prio-leverantör')}
                        </BtButton>
                      </div>
                    )}
                  >
                    {!isProfileComplete &&<Link style={{ color: 'var(--gray-700)' }} to='supplier-presentation'>{t('pricing.supplierPresentationIncomplete', 'Du behöver fylla i din profil först.')}</Link>}
                  </TooltipContainer>
                  <BtButton
                    onClick={() => {
                      window.open('https://accurator.neetocal.com/accuratorleverantor1', '_blank');
                    }}
                    color="white"
                    size="base"
                  >
                    {t('pricing.oneMonth.secondaryButtonText', 'Boka genomgång')}
                  </BtButton>
                </div>
              </div>
            </div>
              </>
            )}

            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('faq.title', 'Vanliga frågor')}</div>
              <div className={styles.faqSectionContent}>
                <div className={styles.faqList}>
                  {faqItems.map((item, index) => (
                    <div key={index} className={styles.faqItem}>
                      <div className={styles.faqQuestion}>{item.question}</div>
                      <p className={styles.faqAnswer}>{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
          </>
        )}
        {selectedTab === 1 && !loading && (
          <>
            {!adInfo?.isFeatured && !lastFeaturedApplication && (
              <Card className={styles.card}>
                <h3>{t('adApplication.intro.heading', 'Accurator Annons')}</h3>
                <Trans t={t} i18nKey="adApplication.intro.body" components={{b: <b />}} />

                <p>{t('adApplication.usp.heading', 'Vi hjälper dig att:')}</p>
                <ul role="list" className={styles.list}>
                  <Trans t={t} i18nKey="adApplication.usp.list" components={{li: <li />}} />
                </ul>

                <p><b>{t('adApplication.outro.heading', 'Har du en tillfällig kampanj, luckor i fabriken eller en helt ny tjänst eller produkt du vill visa upp?')}</b></p>
                <Trans t={t} i18nKey="adApplication.outro.body" />

                <TooltipContainer renderReferenceComponent={(className, ref) => (
                  <div
                    className={className}
                    ref={ref}
                    style={{ width: 'fit-content', alignSelf: 'center', marginTop: '24px' }}
                  >
                    <BtButton
                      disabled={!appState.hasSupplierProfileBeenUpdated()}
                      onClick={() => {
                        setIsApplicationModalVisible(true);
                        setAdApplication({
                          errors: {},
                          startDate: moment().toDate(),
                          title: '',
                          description: '',
                          url: '',
                          image: undefined,
                          applicationType: AdApplicationType.FEATURED,
                          invoiceInterval: undefined,
                          invoiceEmail: appState.getInvoiceEmail(),
                          invoiceRef: appState.getInvoiceRef(),
                        })
                      }}
                    >
                      {t('adApplication.button.apply', 'Ansök om att annonsera på startsidan')}
                    </BtButton>
                  </div>
                )}>
                  {!appState.hasSupplierProfileBeenUpdated() && <span>
                    <Trans t={t} i18nKey="adApplication.button.tooltip.supplierProfileNotUpdated">
                      Fyll i <a href={`/${appState.getCurrentOrgSlug()}/supplier-presentation`} style={{ color: 'var(--gray-700)' }}>leverantörspresentationen</a> först.
                    </Trans>
                  </span>}
                </TooltipContainer>
              </Card>
            )}

            {!!lastFeaturedApplication && (
              <ApplicationStatusCard
                isPending={isFeaturedApplicationPending}
                applicationType={AdApplicationType.FEATURED}
                rejectionReason={lastFeaturedApplication?.rejectionReason ?? ''}
              />
            )}

            {adInfo?.isFeatured && (
              <Card title={t('adApplication.activeAds.title', 'Aktiva annonser')} className={styles.activeFeatureSupplierAd}>
                <div className={styles.activeRecommendedAdsItem}>
                  <p>{t('adApplication.activeAds.dates', 'Datum')}</p>
                  <span>{formatDate(adInfo?.featuredInfo?.startDate)} - {formatDate(adInfo?.featuredInfo?.endDate)}</span>
                </div>
                {(adInfo?.featuredInfo?.image) && (
                  <NewsCard
                    image={adInfo.featuredInfo.image}
                    title={adInfo.featuredInfo?.title ?? ''}
                    description={adInfo.featuredInfo?.description ?? ''}
                    link={adInfo.featuredInfo?.url}
                    adPreviewMode
                  />
                )}
              </Card>
            )}

            {adInfo?.isFeatured && adInfo?.featuredInfo?.startDate && (
              <TitleWrapper title={t('adApplication.statistics.title', 'Statistik')}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                  <ReceivedBidRequestStats requests={requests} adStartDate={moment(adInfo?.featuredInfo?.startDate).toDate()} />
                </div>
              </TitleWrapper>
            )}

            {hasActiveFeaturedAd && (
              <Card title="" className={styles.result}>
                <p>{t('adApplication.activeFeaturedAd.intro', 'Du har lagts till i följande leverantörsdatabaser sedan annonsens början:')}</p>
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>{t('adApplication.activeFeaturedAd.header.supplierName', 'Beställare')}</th>
                      <th>{t('adApplication.activeFeaturedAd.header.createdAt', 'Datum')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adInfo?.addedFeaturedSuppliers?.map(supplier => {
                      return (
                        <tr key={supplier.name}>
                          <td>{supplier.name}</td>
                          <td>{formatDate(supplier.createdAt)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Card>
            )}
          </>
        )}
        {adApplication && (
          <AdModal
            title={t('adModal.title', 'Ansökan')}
            show={isApplicationModalVisible}
            setShow={(value) => setIsApplicationModalVisible(value) }
            adApplication={adApplication}
            setAdApplication={setAdApplication}
            submitFeaturedSupplierAd={submitFeaturedSupplierAd}
            submitRecommendedSupplierAd={submitRecommendedSupplierAd}
            submitting={submitting}
            setSubmitting={setSubmitting}
            areas={areas}
          />
        )}
      </div>
    </AppLayout>
  );
}

export default withRouter(PageMarketing);